import * as Tooltip from "@radix-ui/react-tooltip";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Moon, Sparkles, UserX, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { badgeStyle } from "@/lib/badges";

export interface BadgeProps {
  code: string;
  className?: string;
}

type IconDescriptor = {
  Icon: LucideIcon;
  iconClass: string;
  description: string;
};

/** Lucide icon + explanatory copy per badge code. Unknown codes fall
 * through to a plain text chip. */
const ICON_MAP: Record<string, IconDescriptor> = {
  manager: {
    Icon: Users,
    iconClass: "text-text-2",
    description: "Tiene personas a su cargo en la organización.",
  },
  inactive: {
    Icon: Moon,
    iconClass: "opacity-60",
    description: "Sin actividad registrada en el último periodo.",
  },
  new: {
    Icon: Sparkles,
    iconClass: "text-brand-3",
    description: "Incorporación reciente al leaderboard.",
  },
  deactivated: {
    Icon: UserX,
    iconClass: "text-amber-400",
    description: "Usuario desactivado de la organización.",
  },
};

/** Small inline chip used on ranking rows / podium cards. Tokenized
 * styles live in lib/badges.ts so adding a new code is one edit.
 * Known codes render a matching lucide icon and a Radix tooltip with
 * a one-line explanation; the `new` badge gets a subtle 3s pulse to
 * draw a little bit of attention without stealing it. */
export function Badge({ code, className }: BadgeProps) {
  const { label, className: styleClass } = badgeStyle(code);
  const icon = ICON_MAP[code];
  const reduced = useReducedMotion();

  const baseClasses = cn(
    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
    styleClass,
    className,
  );

  const pulse =
    code === "new" && !reduced
      ? { scale: [1, 1.05, 1], opacity: [1, 0.92, 1] }
      : undefined;

  const content = (
    <motion.span
      className={baseClasses}
      animate={pulse}
      transition={
        code === "new" && !reduced
          ? { duration: 3, repeat: Infinity, ease: "easeInOut" }
          : undefined
      }
    >
      {icon && (
        <icon.Icon
          aria-hidden
          className={cn("h-3 w-3 shrink-0", icon.iconClass)}
        />
      )}
      {label}
    </motion.span>
  );

  if (!icon) return content;

  return (
    <Tooltip.Provider delayDuration={180} skipDelayDuration={80}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span tabIndex={0} className="cursor-help outline-none">
            {content}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={6}
            className="z-50 max-w-xs rounded-lg border border-border-2 bg-bg-1/95 px-3 py-2 text-xs leading-relaxed text-text-2 shadow-lg backdrop-blur"
          >
            <div className="font-semibold text-text-1">{label}</div>
            <div className="mt-0.5 text-text-3">{icon.description}</div>
            <Tooltip.Arrow className="fill-bg-1" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
